import { Injectable } from '@angular/core';

import { STORAGE_KEY } from '@app/core/config/app.config';
import { NonIntegratedAppFacade } from '@app/core/facades/non-integrated-app.facade';
import { Subject } from '@app/core/models/subject.model';
import { NonMapSubjectService } from '@app/core/services/subject/non-map-subject.service';
import { LearningAppsFacade } from '@app/modules/learning-apps/facades/learning-apps-facade';

import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class NonMapSubjectState {
  private supportedSubjects: string[] = [];
  private readonly subjectId = new BehaviorSubject<string>(localStorage.getItem(STORAGE_KEY.nonMapSubject) || '');
  private readonly subjects = new BehaviorSubject<Subject[]>([]);
  public subjects$ = combineLatest([
    this._appFacade.appId$,
    this.subjects.asObservable(),
    this._learningAppsFacade.learningApps$,
  ]).pipe(
    tap(([_appId, subjects]) => {
      if (!subjects.length) {
        this.list(false);
      }
    }),
    filter(([_appId, subjects]) => Boolean(subjects.length)),
    map(([_appId, subjects]) => subjects.filter((subject) => this.isSupported(subject))),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public subjectId$ = combineLatest([this.subjects$, this.subjectId]).pipe(
    filter(([subjects]) => Boolean(subjects && subjects.length)),
    map(([subjects, id]) => (subjects.some((item) => item.id === id) ? id : subjects[0].id)),
    distinctUntilChanged()
  );

  public subject$ = combineLatest([this.subjectId$, this.subjects$]).pipe(
    filter(([id, subjects]) => Boolean(id && subjects)),
    map(([id, subjects]) => subjects.find((subject) => subject.id === id)),
    filter((subject) => Boolean(subject))
  );

  public constructor(
    private readonly _service: NonMapSubjectService,
    private readonly _learningAppsFacade: LearningAppsFacade,
    private readonly _appFacade: NonIntegratedAppFacade
  ) {
    combineLatest([this._appFacade.appId$, this._appFacade.allApps$, this._learningAppsFacade.learningApps$]).subscribe(
      ([selectedId, allApps, learningApps]) => {
        const selectedApp = allApps.find((app) => app.id === selectedId).learningAppName;
        this.supportedSubjects = learningApps
          ?.filter((item) => item.learningAppName === selectedApp)
          .map((value) => value.subjectName);
      }
    );
  }

  public updateSubjectId(id: string): void {
    this.subjectId.next(id);
    localStorage.setItem(STORAGE_KEY.nonMapSubject, id);
  }

  private isSupported(subject: Subject): boolean {
    return this.supportedSubjects.indexOf(subject.name) !== -1;
  }

  private list(onlyMapSubjects: boolean): void {
    this._service.list(onlyMapSubjects).subscribe((subjects) => this.updateSubjects(subjects));
  }

  private updateSubjects(values: Subject[]): void {
    this.subjects.next([...values]);
  }
}
